import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatCurrency } from '../../../utils/formatters';
import { SAVE_DEAL_INTEREST } from '../../../graphql/mutations/dealMutations';
import { openProfileDrawer } from './profileDrawerHelper';
import DealInterestCard from './DealInterestCard';
import DealInterestListItem from './DealInterestListItem';

// Try to import SuccessionThemeProvider, fall back gracefully if not available
let useSuccessionTheme;
try {
  const SuccessionThemeModule = require('../SuccessionThemeProvider');
  useSuccessionTheme = SuccessionThemeModule.useSuccessionTheme;
} catch (error) {
  // SuccessionThemeProvider not available, we'll handle this gracefully
  useSuccessionTheme = null;
}

// Define the inviteUser mutation
const INVITE_USER = gql`
  mutation InviteUser($deal_interest: String!) {
    inviteUser(deal_interest: $deal_interest)
  }
`;

const GET_COMPANY_SETTINGS = gql`
  query GetCompanySettings {
    company {
      id
      name
      agent_word
      agent_word_fr
      deal_room_branded_name
      deal_room_branded_name_fr
      deal_room_single_word
      deal_room_single_word_fr
      stylesheet_name
      business_type
      avatar_background_color
      avatar_directory
      primary_color
    }
  }
`;

const GET_DEAL_MATCHES = gql`
  query GetDealMatches($dealId: String!) {
    getDeal(name: $dealId) {
      id
      deal_name
      headline
      sub_headline
      closed
      evaluate {
        id
        workflow_state
        saved
        invited
        shortlisted
        reason_for_suggestion
        deal_interest_type
        inventory_user {
          id
          user {
            id
            allowed_name
            user_name
            headline
            city
            province
            assets_under_management
            annual_revenue
            motivation
            fb_user_name
            profile_picture
            years_practicing
            direct_revenue
            transactional_revenue
            fee_based_revenue
            trailer_revenue
            number_of_clients
          }
        }
      }
      matches_count
      number_of_invites
      number_of_proposals
      number_of_ndas
      number_of_negotiations
    }
  }
`;

const DealEvaluateMatches = ({ deal, refetchDeal, activeFilter = 'all', filters = [], onFilterChange }) => {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('grid');
  
  // Use succession theme if available
  let colors = null;
  let themeColors = null;
  
  if (useSuccessionTheme) {
    try {
      const { theme, isLightTheme, isDarkTheme, company } = useSuccessionTheme();
      
      // Create color object with actual company colors
      colors = {
        primary: company?.primary_color || '#5489B6',
        secondary: company?.secondary_color || '#002949',
        accent: company?.accent_color || '#5B5E5A',
        neutral: company?.neutral_color || '#666666',
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
      };

      // Create theme-aware color system
      themeColors = {
        bgPrimary: isDarkTheme ? '#1f2937' : '#ffffff',
        bgSecondary: isDarkTheme ? '#111827' : '#f9fafb',
        bgTertiary: isDarkTheme ? '#374151' : '#e5e7eb',
        textPrimary: isDarkTheme ? '#f9fafb' : '#111827',
        textSecondary: isDarkTheme ? '#d1d5db' : '#6b7280',
        textMuted: isDarkTheme ? '#9ca3af' : '#9ca3af',
        border: isDarkTheme ? '#374151' : '#e5e7eb',
        shadow: isDarkTheme ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        shadowLg: isDarkTheme ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      };
    } catch (error) {
      // Fallback to default values if theme context fails
      console.warn('Failed to use SuccessionThemeProvider in DealEvaluateMatches, falling back to default styling');
    }
  }

  // Fallback colors when theme is not available
  const fallbackColors = {
    primary: '#5489B6',
    secondary: '#002949',
    accent: '#5B5E5A',
    neutral: '#666666',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  };

  const fallbackThemeColors = {
    bgPrimary: '#ffffff',
    bgSecondary: '#f9fafb',
    bgTertiary: '#e5e7eb',
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    border: '#e5e7eb',
    shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  };

  // Use theme colors or fallback
  const finalColors = colors || fallbackColors;
  const finalThemeColors = themeColors || fallbackThemeColors;
  
  const { loading: settingsLoading, data: settingsData } = useQuery(GET_COMPANY_SETTINGS);
  const { loading, error, data, refetch } = useQuery(GET_DEAL_MATCHES, {
    variables: { dealId: deal.deal_name },
    skip: !deal.deal_name
  });
  
  const [saveDealInterest] = useMutation(SAVE_DEAL_INTEREST);
  const [inviteUser] = useMutation(INVITE_USER);
  
  const companySettings = settingsData?.company || {};
  const dealData = data?.getDeal || {};
  const agentWord = intl.locale === 'fr' ? (companySettings?.agent_word_fr || 'conseiller') : (companySettings?.agent_word || 'advisor');
  const agentWordLower = agentWord.toLowerCase();
  const agentWordLabel = agentWordLower.charAt(0).toUpperCase() + agentWordLower.slice(1);
  const agentWordPlural = agentWordLower.endsWith('s') ? agentWordLower : `${agentWordLower}s`;
  console.log(companySettings);
  // Handler functions for the mutations
  const handleSaveDealInterest = (dealInterestId) => {
    saveDealInterest({
      variables: {
        dealInterestId: dealInterestId
      },
      update: (cache) => {
        // Update cache for immediate UI update
        const dealInterestData = cache.identify({ __typename: 'DealInterest', id: dealInterestId });
        cache.modify({
          id: dealInterestData,
          fields: {
            saved: () => true
          }
        });
        refetch().then(() => {
          // Update the dealInterests array in the deal data
          cache.modify({
            id: cache.identify({ __typename: 'Deal', id: deal.id }),
            fields: {
              evaluate: (existingData) => {
                const updatedEvaluate = existingData.map(di => 
                  di.id === dealInterestId ? { ...di, saved: true } : di
                );
                return updatedEvaluate;
              }
            }
          });
        }).catch(error => {
          console.error("Error saving advisor:", error);
          alert(intl.formatMessage({ id: "succession_deals.wizard.steps.deal_evaluate_matches.alerts.error_saving_advisor" }));
        });
      }
    }).then((response) => {
      // Check for errors in the response
      if (response.data?.saveDealInterest?.errors?.length) {
        console.error("Error saving advisor:", response.data.saveDealInterest.errors);
        alert(intl.formatMessage({ id: "succession_deals.wizard.steps.deal_evaluate_matches.alerts.error_saving_advisor" }) + ": " + response.data.saveDealInterest.errors.join(", "));
        return;
      }
      
      
      // Refetch the deal data to update counts
      if (refetchDeal && typeof refetchDeal === 'function') {
        refetchDeal();
      }
    }).catch(error => {
      console.error("Error saving advisor:", error);
      alert(intl.formatMessage({ id: "succession_deals.wizard.steps.deal_evaluate_matches.alerts.error_saving_advisor" }));
    });
  };
  
  const handleInviteDealInterest = (dealInterestId) => {
    console.log("Inviting user with deal interest ID:", dealInterestId);
    
    inviteUser({
      variables: {
        deal_interest: dealInterestId
      },
      update: (cache) => {
        try {
          // Update cache for immediate UI update
          const dealInterestData = cache.identify({ __typename: 'DealInterest', id: dealInterestId });
          cache.modify({
            id: dealInterestData,
            fields: {
              invited: () => true,
              workflow_state: () => 'invited'
            }
          });
        } catch (error) {
          console.error("Error updating cache:", error);
        }
      }
    }).then((response) => {
      if (response.data?.inviteUser === true) {
        // Show success feedback
        
        // Refetch the deal data to update counts
        if (refetchDeal && typeof refetchDeal === 'function') {
          refetchDeal();
        } else {
          // If refetchDeal isn't available, use the component's refetch function
          refetch();
        }
      } else {
        console.error("Error with invitation response:", response);
        alert(intl.formatMessage({ id: "succession_deals.wizard.steps.deal_evaluate_matches.alerts.error_inviting_advisor" }));
      }
    }).catch(error => {
      console.error("Error inviting advisor:", error);
      alert(intl.formatMessage({ id: "succession_deals.wizard.steps.deal_evaluate_matches.alerts.error_inviting_advisor" }));
    });
  };
  
  // Determine advisor type for each deal interest
  const getAdvisorType = (dealInterest) => {
    if (!dealInterest) return null;
    
    // First check workflow_state which is most reliable
    if (dealInterest.deal_interest_type === 'following') {
      return 'rolodex'; // Following/rolodex
    } else if (dealInterest.deal_interest_type === 'follower') {
      return 'follower'; // Follower
    }
    
    // Then check for reason_for_suggestion which indicates a match
    if (dealInterest.deal_interest_type === 'suggested_match') {
      return 'matches'; // Suggested match
    }
    
    return null;
  };

  // Helper function to get badge text based on advisor type
  const getAdvisorBadgeInfo = (dealInterest) => {
    if (!dealInterest) return null;
    
    const type = getAdvisorType(dealInterest);

    // Create badge info object based on type
    if (type === 'matches') {
      return { text: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_evaluate_matches.badges.suggested_match' }), color: 'green' };
    } else if (type === 'rolodex') {
      return { text: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_evaluate_matches.badges.rolodex' }), color: 'blue' };
    } else if (type === 'follower') {
      return { text: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_evaluate_matches.badges.follower' }), color: 'purple' };
    }
    
    // If we can't determine the type, still provide a default badge
    return { text: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_evaluate_matches.badges.potential_match' }), color: 'gray' };
  };
  
  // Filter and search functions
  const filteredMatches = () => {
    if (!dealData?.evaluate || !Array.isArray(dealData.evaluate)) return [];
    
    let filtered = dealData.evaluate;
    
    // Apply active filter
    if (activeFilter && activeFilter !== 'all') {
      if (activeFilter === 'saved') {
        // Filter for saved advisors
        filtered = filtered.filter(di => di && di.saved);
      } else if (activeFilter === 'matches') {
        // Filter for suggested matches
        filtered = filtered.filter(di => getAdvisorType(di) === 'matches');
      } else if (activeFilter === 'rolodex') {
        // Filter for rolodex/following
        filtered = filtered.filter(di => getAdvisorType(di) === 'rolodex');
      } else if (activeFilter === 'follower') {
        // Filter for followers
        filtered = filtered.filter(di => getAdvisorType(di) === 'follower');
      }
    }
    
    // Apply search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (di) => di && di.inventory_user && di.inventory_user.user && (
          (di.inventory_user.user.name || '').toLowerCase().includes(term) ||
          (di.inventory_user.user.user_name || '').toLowerCase().includes(term) ||
          (di.inventory_user.user.headline || '').toLowerCase().includes(term) ||
          (di.inventory_user.user.city || '').toLowerCase().includes(term) ||
          (di.inventory_user.user.province || '').toLowerCase().includes(term)
        )
      );
    }
    
    return filtered;
  };
  
  if (loading || settingsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-succession-primary" role="status">
          <span className="visually-hidden">
            <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.loading_message" />
          </span>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-succession-error-bg border border-succession-error-border rounded-md p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-succession-error" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-succession-error-text">
              <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.error_loading_data" />
            </h3>
            <div className="mt-2 text-sm text-succession-error-text">
              <p>{error.message}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const matches = filteredMatches();
  
  const filterLabels = {
    all: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.all' }),
    matches: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.suggested_matches' }),
    rolodex: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.rolodex' }),
    follower: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.followers' }),
    saved: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.filters.saved' })
  };
  const filterLabel = filterLabels[activeFilter];

  return (
    <div>
      {/* Search and View Options */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-xl font-bold text-succession-text-primary mb-4 md:mb-0">
          {intl.formatMessage(
            { id: 'succession_deals.wizard.steps.deal_evaluate_matches.title' },
            { agent_word: agentWordLabel }
          )} ({matches.length})
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="relative" style={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px' }}>
            <input
              type="text"
              placeholder={intl.formatMessage(
                { id: "succession_deals.wizard.steps.deal_evaluate_matches.search_placeholder" },
                { agent_word: agentWordLower }
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '10px',
                borderRadius: '9999px',
                backgroundColor: 'var(--succession-bg-secondary, #f8fafc)',
                border: '1px solid var(--succession-border, #64748b)',
                color: 'var(--succession-text-primary, #0f172a)',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxShadow: 'none',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--succession-primary, #5489B6)';
                e.target.style.boxShadow = '0 0 0 3px rgba(84, 137, 182, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--succession-border, #64748b)';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div className="absolute left-3 top-3 text-succession-text-muted" style={{ pointerEvents: 'none' }}>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="flex border border-succession-border rounded-lg overflow-hidden flex-shrink-0">
            <button
              onClick={() => setView('grid')}
              className={`px-4 py-2 flex items-center ${view === 'grid' ? 'bg-succession-bg-secondary text-succession-primary' : 'text-succession-text-muted'}`}
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
                              <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.view_options.grid" />
              </button>
              <button
                onClick={() => setView('list')}
                className={`px-4 py-2 flex items-center ${view === 'list' ? 'bg-succession-bg-secondary text-succession-primary' : 'text-succession-text-muted'}`}
              >
                <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.view_options.list" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Filters - show if available */}
      {filters && filters.length > 0 && onFilterChange && (
        <div className="mb-6 bg-succession-bg-secondary border border-succession-border rounded-lg shadow-sm p-4">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium text-succession-text-primary mr-2">
              <FormattedMessage id="succession_deals.wizard.steps.deal_prospects.filters.filter_by" />
            </span>
            {filters.map(filter => (
              <button
                key={filter.id}
                onClick={() => onFilterChange(filter.id)}
                className={`px-3 py-1 text-sm rounded-full ${
                  activeFilter === filter.id
                    ? 'bg-succession-primary-light text-succession-primary font-medium' 
                    : 'bg-succession-bg-light text-succession-text-secondary hover:bg-succession-bg-secondary'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Stage description */}
      <div className="mb-6 p-4 bg-succession-bg-secondary rounded-md">
          <p className="text-sm text-succession-text-secondary">
            <strong>
              <FormattedMessage
                id="succession_deals.wizard.steps.deal_evaluate_matches.stage_description.title"
              />
            </strong>{' '}
            <FormattedMessage
              id="succession_deals.wizard.steps.deal_evaluate_matches.stage_description.description"
              values={{ agent_word: agentWordLower }}
            />
            {activeFilter !== 'all' && (
              <span className="ml-2 font-bold">
                <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.stage_description.filtering_by" /> {filterLabel || activeFilter}
              </span>
            )}
          </p>
        </div>
      
      {/* No matches message */}
      {matches.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-succession-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-succession-text-primary">
            {intl.formatMessage(
              { id: 'succession_deals.wizard.steps.deal_evaluate_matches.empty_state.title' },
              { agent_word: agentWordLower }
            )}
          </h3>
            <p className="mt-1 text-sm text-succession-text-muted">
              {searchTerm || activeFilter !== 'all' 
                ? <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.empty_state.description_with_filter" /> 
                : <FormattedMessage
                    id="succession_deals.wizard.steps.deal_evaluate_matches.empty_state.description_no_matches"
                    values={{ agent_word: agentWordLower }}
                  />}
            </p>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {view === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {matches.map((dealInterest) => {
                const badgeInfo = getAdvisorBadgeInfo(dealInterest);
                return (
                  <DealInterestCard
                    key={dealInterest.id}
                    dealInterest={dealInterest}
                    dealName={deal.deal_name}
                    companySettings={companySettings}
                    onSave={handleSaveDealInterest}
                    onInvite={handleInviteDealInterest}
                    badgeText={badgeInfo?.text}
                    badgeColor={badgeInfo?.color}
                    onViewDetails={() => {
                      if (dealInterest?.inventory_user?.user?.fb_user_name) {
                        openProfileDrawer(dealInterest.inventory_user.user.fb_user_name, { fromDeal: true, dealInterest });
                      }
                    }}
                                              viewDetailsLabel={intl.formatMessage({ id: "succession_deals.wizard.steps.deal_evaluate_matches.actions.view_profile" })}
                  />
                );
              })}
            </div>
          )}
          
          {/* List View */}
          {view === 'list' && (
            <div className="overflow-x-auto border border-succession-border rounded-lg">
              <table className="min-w-full divide-y divide-succession-border">
                <thead className="bg-succession-bg-secondary">
                  <tr>
                                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider w-80">
                        {agentWordLabel}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                        <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.table_headers.location" />
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                        <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.table_headers.assets" />
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                        <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.table_headers.revenue" />
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-succession-text-muted uppercase tracking-wider">
                        <FormattedMessage id="succession_deals.wizard.steps.deal_evaluate_matches.table_headers.actions" />
                      </th>
                  </tr>
                </thead>
                <tbody className="bg-succession-bg-primary divide-y divide-succession-border">
                  {matches.map((dealInterest) => {
                    const badgeInfo = getAdvisorBadgeInfo(dealInterest);
                    return (
                      <DealInterestListItem
                        key={dealInterest.id}
                        dealInterest={dealInterest}
                        dealName={deal.deal_name}
                        companySettings={companySettings}
                        onSave={handleSaveDealInterest}
                        onInvite={handleInviteDealInterest}
                        badgeText={badgeInfo?.text}
                        badgeColor={badgeInfo?.color}
                        onViewDetails={() => {
                          if (dealInterest?.inventory_user?.user?.fb_user_name) {
                            openProfileDrawer(dealInterest.inventory_user.user.fb_user_name, { fromDeal: true, dealInterest });
                          }
                        }}
                        viewDetailsLabel={intl.formatMessage({ id: "succession_deals.wizard.steps.deal_evaluate_matches.actions.view_profile" })}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

DealEvaluateMatches.propTypes = {
  deal: PropTypes.object,
  refetchDeal: PropTypes.func.isRequired,
  activeFilter: PropTypes.string
};

export default DealEvaluateMatches; 